import { assignmentTitle } from "@produce/shared";
import type { CrewShiftState } from "@/hooks/useCrewShiftProgress";
import type { TimeFormatter } from "@/hooks/useTimeFormatter";

/**
 * The "NOW / NEXT UP" pair shown under a crew member's name in the list
 * beside the producer's live-tracking map.
 *
 * The list used to say only how long someone had left, without saying
 * what they were on. Both blocks read the same shift state that fills the
 * ring on their marker, so the list, the map and the popup always agree.
 */
export const CrewShiftLines = ({
  shift,
  formatTime,
}: {
  shift: CrewShiftState | undefined;
  /** From the page that hosts this list; see ScheduleFeedbackMarker for why. */
  formatTime: TimeFormatter;
}) => (
  <div className="mt-2 space-y-1.5">
    <ShiftBlock
      formatTime={formatTime}
      label="NOW"
      tone="text-primary"
      assignment={shift?.current}
      timeLeftLabel={shift?.timeLeftLabel}
      emptyLabel="No assignment right now"
    />
    {shift?.next && (
      <ShiftBlock
        formatTime={formatTime}
        label="NEXT UP"
        tone="text-muted-foreground"
        assignment={shift.next}
      />
    )}
  </div>
);

const ShiftBlock = ({
  formatTime,
  label,
  tone,
  assignment,
  timeLeftLabel,
  emptyLabel,
}: {
  label: string;
  tone: string;
  assignment: CrewShiftState["current"] | undefined;
  timeLeftLabel?: string | null;
  /** Rendered instead of the assignment when there is none. */
  emptyLabel?: string;
  formatTime: TimeFormatter;
}) => (
  <div>
    <span className={`text-[10px] font-bold tracking-wider ${tone}`}>{label}</span>
    {assignment ? (
      <>
        <p className="truncate text-xs font-medium">{assignmentTitle(assignment)}</p>
        <p className="text-xs text-muted-foreground">
          {formatTime(assignment.filming_start)} – {formatTime(assignment.filming_end)}
          {timeLeftLabel && (
            <>
              {" · "}
              <span className="font-medium text-primary">{timeLeftLabel}</span>
            </>
          )}
        </p>
      </>
    ) : (
      emptyLabel && <p className="text-xs text-muted-foreground">{emptyLabel}</p>
    )}
  </div>
);
